/**
 * Lighthouse uses 2.625 DPI for calculations, the device simulation
 * is set to 3 DPI.
 *
 * For this reason we cannot have an image of DPI 3 in our source set,
 * as the device will pick 3 DPI image and the audit will calculate a
 * max width/height with 2.625 DPI.`
 *
 * source:
 * https://github.com/GoogleChrome/lighthouse/blob/master/lighthouse-core/config/constants.js#L63 - Devices used in Lighthouse
 * https://github.com/GoogleChrome/lighthouse/blob/master/lighthouse-core/audits/image-size-responsive.js#L286
 * https://github.com/GoogleChrome/lighthouse/commit/a6738e0033e7e5ca308b97c1c36f298b7d399402 - 16 Dec 2020
 */

import { metaImageDimensions } from './constants';

export const newsListImageConfigurations = [
  {
    width: 42,
    height: 42,
    quality: 10,
    resize: 'FILL_DOWN',
  },
  {
    width: 80, height: 80, resize: 'FILL_DOWN',
  },
  {
    width: 160, height: 160, resize: 'FILL_DOWN',
  },
  {
    width: 320, height: 320, resize: 'FILL_DOWN',
  },
];

export const newsFeedImageConfigurations = [
  {
    width: 42,
    height: 28,
    quality: 10,
    resize: 'FILL_DOWN',
  },
  {
    width: 201, height: 134, resize: 'FILL_DOWN',
  },
  {
    width: 390, height: 260, resize: 'FILL_DOWN',
  },
  {
    width: 402, height: 268, resize: 'FILL_DOWN',
  },
  {
    width: 603, height: 402, resize: 'FILL_DOWN',
  },
];

export const categoryListImageConfigurations = [
  {
    width: 42,
    height: 28,
    quality: 10,
    resize: 'FILL_DOWN',
  },
  {
    width: 330, height: 220, resize: 'FILL_DOWN',
  },
  {
    width: 660, height: 440, resize: 'FILL_DOWN',
  },
  {
    width: 720, height: 480, resize: 'FILL_DOWN',
  },
];

export const articleListImageConfigurations = [
  {
    width: 42,
    height: 28,
    quality: 10,
    resize: 'FILL_DOWN',
  },
  {
    width: 312, height: 208, resize: 'FILL_DOWN', // desktop * 1 dpi
  },
  {
    width: 672, height: 448, resize: 'FILL_DOWN',
  },
  {
    width: 720, height: 480, resize: 'FILL_DOWN', // mobile * 2 dpi
  },
];

export const articleSearchImageConfigurations = [
  {
    width: 10,
    height: 7,
    quality: 10,
    resize: 'FILL_DOWN',
  },
  {
    width: 102, height: 68, resize: 'FILL_DOWN',
  },
  {
    width: 204, height: 136, resize: 'FILL_DOWN',
  },
  {
    width: 306, height: 204, resize: 'FILL_DOWN',
  },
];

const metaImageConfiguration = {
  ...metaImageDimensions, resize: 'FIT', format: 'JPEG',
};

export const articleImageConfigurations = [
  metaImageConfiguration,
  {
    width: 42,
    height: 28,
    quality: 10,
    resize: 'FILL_DOWN',
  },
  {
    width: 360, height: 240, resize: 'FILL_DOWN', // mobile * 1 dpi
  },
  {
    width: 694, height: 463, resize: 'FILL_DOWN',
  },
  {
    width: 720, height: 480, resize: 'FILL_DOWN', // mobile * 2 dpi
  },
  {
    width: 1388, height: 926, resize: 'FILL_DOWN',
  },
];

export const ampArticleImageConfigurations = [
  metaImageConfiguration,
  {
    width: 42, height: 28, format: 'WEBP', quality: 1, resize: 'FILL_DOWN',
  },
  {
    width: 42, height: 28, format: 'JPEG', quality: 10, resize: 'FILL_DOWN',
  },
  {
    width: 320, height: 213, format: 'WEBP', resize: 'FILL_DOWN',
  },
  {
    width: 320, height: 213, format: 'JPEG', resize: 'FILL_DOWN',
  },
  {
    width: 375, height: 250, format: 'WEBP', resize: 'FILL_DOWN',
  },
  {
    width: 375, height: 250, format: 'JPEG', resize: 'FILL_DOWN',
  },
  {
    width: 414, height: 276, format: 'WEBP', resize: 'FILL_DOWN',
  },
  {
    width: 414, height: 276, format: 'JPEG', resize: 'FILL_DOWN',
  },
  {
    width: 725, height: 483, format: 'WEBP', resize: 'FILL_DOWN',
  },
  {
    width: 725, height: 483, format: 'JPEG', resize: 'FILL_DOWN',
  },
  {
    width: 740, height: 493, format: 'WEBP', resize: 'FILL_DOWN',
  },
  {
    width: 740, height: 493, format: 'JPEG', resize: 'FILL_DOWN',
  },
];

export const companyProfilesCardImageConfigurations = [
  {
    width: 42,
    height: 28,
    quality: 10,
    resize: 'FILL_DOWN',
  },
  {
    width: 288, height: 192, resize: 'FILL_DOWN',
  },
  {
    width: 318, height: 212, resize: 'FILL_DOWN',
  },
  {
    width: 576, height: 384, resize: 'FILL_DOWN',
  },
  {
    width: 636, height: 424, resize: 'FILL_DOWN',
  },
  {
    width: 960, height: 640, resize: 'FILL_DOWN',
  },
];

export const directoryListImageConfigurations = [
  { width: 30, quality: 10 },
  { width: 60 },
  { width: 120 },
  { width: 180 },
];

export const directoryCardImageConfigurations = [
  {
    width: 42,
    height: 28,
    quality: 10,
    resize: 'FILL_DOWN',
  },
  {
    width: 318, height: 212, resize: 'FILL_DOWN',
  },
  {
    width: 636, height: 424, resize: 'FILL_DOWN',
  },
  {
    width: 960, height: 640, resize: 'FILL_DOWN',
  },
];

export const directoryCardLogoConfigurations = directoryCardImageConfigurations;

export const epbCardLogoConfigurations = [
  {
    width: 21,
    height: 14,
    quality: 10,
    resize: 'FIT',
  },
  { width: 240, height: 60, resize: 'FIT' },
  { width: 480, height: 120, resize: 'FIT' },
  { width: 960, height: 240, resize: 'FIT' },
];

export const marketplaceLogoImageConfigurations = [
  { width: 42, quality: 10 },
  { width: 100 },
  { width: 200 },
];

export const marketplaceProductListImageConfigurations = [
  {
    width: 42,
    height: 63,
    quality: 10,
    resize: 'FILL_DOWN',
  },
  {
    width: 312, // desktop * 1 dpi
    height: 468,
    resize: 'FILL_DOWN',
  },
  {
    width: 624, // mobile * 2 dpi
    height: 936,
    resize: 'FILL_DOWN',
  },
  {
    width: 1007, // desktop (1920w) * 2 dpi
    height: 1511,
    resize: 'FILL_DOWN',
  },
];

export const specialsPageImageConfigurationsMobile = [
  {
    width: 42,
    height: 34,
    quality: 10,
    resize: 'FILL_DOWN',
  },
  {
    width: 360, height: 288, resize: 'FILL_DOWN',
  },
  {
    width: 720, height: 576, resize: 'FILL_DOWN',
  },
];

export const specialsPageImageConfigurationsDesktop = [
  {
    width: 42,
    height: 11,
    quality: 10,
    resize: 'FILL_DOWN',
  },
  {
    width: 1350, height: 352, resize: 'FILL_DOWN',
  },
  {
    width: 2560, height: 667, resize: 'FILL_DOWN',
  },
  {
    width: 5120, height: 1334, resize: 'FILL_DOWN',
  },
];

export const allImageConfigurations = [
  ...newsListImageConfigurations,
  ...newsFeedImageConfigurations,
  ...categoryListImageConfigurations,
  ...articleListImageConfigurations,
  ...articleSearchImageConfigurations,
  ...articleImageConfigurations,
  ...ampArticleImageConfigurations,
  ...directoryListImageConfigurations,
  ...directoryCardImageConfigurations,
  ...epbCardLogoConfigurations,
  ...marketplaceLogoImageConfigurations,
  ...marketplaceProductListImageConfigurations,
  ...specialsPageImageConfigurationsMobile,
  ...specialsPageImageConfigurationsDesktop,
];
